import { useEffect, useState, useMemo, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isEditor } from "@/lib/roles";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import SecureDocViewer from "@/components/SecureDocViewer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Plus, Lock, Trash2, FileType, FolderPlus, Pencil, Search } from "lucide-react";
import { toast } from "sonner";

export default function Documents({ category, title, subtitle }) {
  const { user } = useAuth();
  const editor = isEditor(user?.role);
  const isAdmin = user?.role === "admin";
  const scope = category;
  const [docs, setDocs] = useState([]);
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState("all");
  const [q, setQ] = useState("");
  const [pg, setPg] = useState("all");
  const [viewer, setViewer] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", practice_group: "", tags: "", body: "", folder: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [folderOpen, setFolderOpen] = useState(false);
  const [newFolder, setNewFolder] = useState("");

  const load = useCallback(() => {
    api.get("/documents", { params: { category } }).then((r) => setDocs(r.data)).catch((e) => console.error("documents load", e));
    api.get("/folders", { params: { scope } }).then((r) => setFolders(r.data)).catch((e) => console.error("folders load", e));
  }, [category, scope]);
  useEffect(() => { load(); }, [load]);

  const folderNames = useMemo(() => {
    const set = new Set();
    folders.forEach((f) => set.add(f.name));
    docs.forEach((d) => d.folder && set.add(d.folder));
    return Array.from(set).sort();
  }, [folders, docs]);
  const pgNames = useMemo(() => ["all", ...Array.from(new Set(docs.map((d) => d.practice_group).filter(Boolean))).sort()], [docs]);
  const filtered = docs.filter((d) => {
    const mFolder = folder === "all" || d.folder === folder;
    const mPg = pg === "all" || d.practice_group === pg;
    const mQ = !q || `${d.title} ${d.description || ""} ${(d.tags || []).join(" ")} ${d.body || ""}`.toLowerCase().includes(q.toLowerCase());
    return mFolder && mPg && mQ;
  });

  const save = async () => {
    if (!form.title) return toast.error("Title required");
    if (!file && !form.body) return toast.error("Attach a file or enter document text");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title); fd.append("category", category);
      fd.append("practice_group", form.practice_group); fd.append("tags", form.tags);
      fd.append("description", form.description); fd.append("body", form.body);
      fd.append("folder", form.folder || (folder !== "all" ? folder : ""));
      if (file) fd.append("file", file);
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document added"); setOpen(false);
      setForm({ title: "", description: "", practice_group: "", tags: "", body: "", folder: "" }); setFile(null); load();
    } catch { toast.error("Upload failed (editor access required)"); }
    finally { setBusy(false); }
  };
  const saveEdit = async () => {
    try {
      await api.put(`/documents/${editForm.id}`, {
        title: editForm.title, description: editForm.description, practice_group: editForm.practice_group,
        folder: editForm.folder, tags: editForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Document updated"); setEditForm(null); load();
    } catch { toast.error("Update failed"); }
  };
  const remove = async (id) => { await api.delete(`/documents/${id}`); load(); toast.success("Removed"); };
  const createFolder = async () => {
    if (!newFolder.trim()) return toast.error("Folder name required");
    try { await api.post("/folders", { scope, name: newFolder.trim() }); toast.success("Folder created"); setFolderOpen(false); const n = newFolder.trim(); setNewFolder(""); await api.get("/folders", { params: { scope } }).then((r) => setFolders(r.data)); setFolder(n); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not create folder"); }
  };

  const tabs = ["all", ...folderNames];

  return (
    <div data-testid={`documents-${category}`}>
      <PageHeader overline="Secure Repository" title={title} subtitle={subtitle}>
        {isAdmin && (
          <Dialog open={folderOpen} onOpenChange={setFolderOpen}>
            <DialogTrigger asChild><Button variant="outline" data-testid="doc-new-folder-btn"><FolderPlus className="h-4 w-4 mr-1.5" /> New Folder</Button></DialogTrigger>
            <DialogContent className="max-w-sm">
              <DialogHeader><DialogTitle>New Document Folder</DialogTitle><DialogDescription className="sr-only">Create a filing folder.</DialogDescription></DialogHeader>
              <Input data-testid="doc-new-folder-name" placeholder="e.g. Motions, Discovery, Retainers" value={newFolder} onChange={(e) => setNewFolder(e.target.value)} />
              <DialogFooter><Button data-testid="doc-create-folder-btn" onClick={createFolder}>Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        {editor && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-document-btn"><Plus className="h-4 w-4 mr-1.5" /> Add Document</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Add Document</DialogTitle><DialogDescription className="sr-only">Add a document.</DialogDescription></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs">Title</Label><Input data-testid="doc-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
                <div><Label className="text-xs">Description</Label><Input data-testid="doc-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label className="text-xs">Folder</Label><Input data-testid="doc-folder" list="doc-folder-list" value={form.folder} onChange={(e) => setForm({ ...form, folder: e.target.value })} /><datalist id="doc-folder-list">{folderNames.map((f) => <option key={f} value={f} />)}</datalist></div>
                  <div><Label className="text-xs">Practice Group</Label><Input data-testid="doc-pg" value={form.practice_group} onChange={(e) => setForm({ ...form, practice_group: e.target.value })} /></div>
                  <div><Label className="text-xs">Tags</Label><Input data-testid="doc-tags" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} /></div>
                </div>
                <div>
                  <Label className="text-xs">Upload File (PDF / image)</Label>
                  <input data-testid="doc-file" type="file" onChange={(e) => setFile(e.target.files[0])}
                    className="block w-full text-sm border border-border rounded-sm p-2 file:mr-3 file:border-0 file:bg-primary file:text-primary-foreground file:px-3 file:py-1 file:rounded-sm" />
                </div>
                <div className="text-center text-xs text-muted-foreground">— or paste document text —</div>
                <div><Textarea data-testid="doc-body" rows={5} placeholder="Document text (for templates)" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} /></div>
              </div>
              <DialogFooter><Button data-testid="save-document-btn" onClick={save} disabled={busy}>{busy ? "Uploading…" : "Save"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      <div className="flex items-center gap-2 text-xs text-muted-foreground bg-secondary/60 border border-border rounded-sm px-3 py-2 mb-4">
        <Lock className="h-3.5 w-3.5 text-primary" /> All documents are view-only. Downloading, printing and copying are disabled and logged for DLP compliance.
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="doc-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, description, tags…" className="pl-9" />
        </div>
        {pgNames.length > 1 && (
          <Select value={pg} onValueChange={setPg}>
            <SelectTrigger className="w-64" data-testid="doc-pg-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{pgNames.map((p) => <SelectItem key={p} value={p}>{p === "all" ? "All Practice Groups" : p}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>

      {tabs.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-5" data-testid="doc-folder-tabs">
          {tabs.map((t) => (
            <button key={t} data-testid="doc-folder-tab" onClick={() => setFolder(t)}
              className={`px-3 py-1.5 text-sm rounded-sm border transition-colors ${folder === t ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}>
              {t === "all" ? "All" : t}
            </button>
          ))}
        </div>
      )}

      {filtered.length === 0 ? <EmptyState icon={FileText} title="No documents here yet" /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="documents-grid">
          {filtered.map((d) => (
            <div key={d.id} className="group border border-border bg-card rounded-sm p-5 hover:border-primary transition-colors animate-fade-up" data-testid="document-card">
              <div className="flex items-start justify-between">
                <div className="grid place-items-center h-10 w-10 bg-primary/10 text-primary rounded-sm mb-3">
                  {d.storage_path ? <FileType className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
                </div>
                {editor && (
                  <div className="flex items-center gap-2">
                    <button data-testid="edit-document-btn" onClick={() => setEditForm({ id: d.id, title: d.title || "", description: d.description || "", practice_group: d.practice_group || "", folder: d.folder || "", tags: (d.tags || []).join(", ") })} className="text-muted-foreground hover:text-primary"><Pencil className="h-4 w-4" /></button>
                    <button data-testid="delete-document-btn" onClick={() => remove(d.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                  </div>
                )}
              </div>
              <button onClick={() => setViewer(d)} data-testid="open-document-btn" className="text-left w-full">
                <div className="text-sm font-semibold group-hover:text-primary transition-colors">{d.title}</div>
                <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{d.description}</div>
                <div className="flex flex-wrap items-center gap-1.5 mt-3">
                  {d.folder && <span className="text-[10px] px-1.5 py-0.5 bg-primary/10 text-primary rounded-sm">{d.folder}</span>}
                  {d.practice_group && <span className="text-[10px] px-1.5 py-0.5 bg-secondary rounded-sm text-muted-foreground">{d.practice_group}</span>}
                  {d.is_sample && <span className="text-[10px] px-1.5 py-0.5 bg-amber-500/10 text-amber-600 rounded-sm">Sample</span>}
                  {d.storage_path && <span className="text-[10px] px-1.5 py-0.5 bg-primary/10 text-primary rounded-sm uppercase">{d.file_ext}</span>}
                </div>
              </button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editForm} onOpenChange={(o) => !o && setEditForm(null)}>
        <DialogContent className="max-w-lg" data-testid="edit-document-dialog">
          <DialogHeader><DialogTitle>Edit Document</DialogTitle><DialogDescription className="sr-only">Edit document metadata.</DialogDescription></DialogHeader>
          {editForm && (
            <div className="space-y-3">
              <div><Label className="text-xs">Title</Label><Input data-testid="doc-edit-title" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} /></div>
              <div><Label className="text-xs">Description</Label><Input data-testid="doc-edit-desc" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs">Folder</Label><Input data-testid="doc-edit-folder" list="doc-folder-list" value={editForm.folder} onChange={(e) => setEditForm({ ...editForm, folder: e.target.value })} /></div>
                <div><Label className="text-xs">Practice Group</Label><Input data-testid="doc-edit-pg" value={editForm.practice_group} onChange={(e) => setEditForm({ ...editForm, practice_group: e.target.value })} /></div>
                <div><Label className="text-xs">Tags</Label><Input data-testid="doc-edit-tags" value={editForm.tags} onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })} /></div>
              </div>
            </div>
          )}
          <DialogFooter><Button data-testid="save-doc-edit-btn" onClick={saveEdit}>Save Changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <SecureDocViewer doc={viewer} open={!!viewer} onOpenChange={(o) => !o && setViewer(null)} viewerName={user?.name} />
    </div>
  );
}
